"use client";

import HorizontalCarousel from "@/components/HorizontalCarousel";
import BannerSkeleton from "@/components/skeletons/card/BannerSkeleton";
import Image from "next/image";
import { type CarouselApi, CarouselItem } from "@/components/ui/carousel";
import { useEffect, useState } from "react";
import { SiteFrame } from "@/components/layout/SiteFrame";
import { useBanner } from "@/hooks/banner/useBanner";
import { Link } from "@/i18n/navigation";

const Banner = () => {
  const { data: banners = [], isLoading } = useBanner();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const sortedBanners = [...banners].sort((a, b) => a.position - b.position);

  useEffect(() => {
    if (!carouselApi || isLoading || sortedBanners.length <= 1) {
      return;
    }

    const autoplayInterval = setInterval(() => {
      if (carouselApi.canScrollNext()) {
        carouselApi.scrollNext();
        return;
      }

      carouselApi.scrollTo(0);
    }, 3500);

    return () => clearInterval(autoplayInterval);
  }, [carouselApi, isLoading, sortedBanners.length]);

  return (
    <section className="w-full">
      <SiteFrame>
        <HorizontalCarousel setApi={setCarouselApi}>
          {isLoading
            ? Array.from({ length: 4 }, (_, index) => (
              <CarouselItem
                key={`banner-skeleton-${index}`}
                className="basis-auto self-stretch pl-0"
              >
                <BannerSkeleton className="h-fit w-[280px] sm:w-[356px]" />
              </CarouselItem>
            ))
            : sortedBanners.map((banner) => {
              return (
                <CarouselItem
                  key={`${banner.position}-${banner.bannerLink}`}
                  className="basis-auto self-stretch pl-0"
                >
                  <Link
                    href={banner.payload.link ?? "https://tovtrip.com"}
                    target={banner.targetType}
                    rel={
                      banner.targetType === "_blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="group relative block h-[120px] w-[280px] overflow-hidden rounded-2xl sm:h-[142px] sm:w-[356px]"
                  >
                    <Image
                      src={banner.bannerLink}
                      alt={banner.title}
                      fill
                      unoptimized
                      sizes="(max-width: 640px) 280px, 356px"
                      className="object-cover object-center transform-gpu transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                      priority
                    />
                  </Link>
                </CarouselItem>
              );
            })}
        </HorizontalCarousel>
      </SiteFrame>
    </section>
  );
};

export default Banner;
